import ResumeItem from "@/common/components/ResumeItem";
import Spinner from "@/common/components/Spinner";
import { cn } from "@/common/utils/cn";
import { getFilenameFromUrl } from "@/common/utils/getFilenameFromUrl";
import { useMaintenanceFiscalAssessmentDocumentGroupsUpdateDocumentsDelete } from "@/features/maintenance/features/workflows/hooks/forwardings/fiscalAssessmentDocumentGroups/useMaintenanceFiscalAssessmentDocumentGroupsUpdateDocumentsDelete";
import { MaintenanceFiscalAssessmentDocumentGroupData } from "@/features/maintenance/features/workflows/types/forwardings/fiscalAssessmentEntry/fiscalAssessmentEntry.type";
import { useQueryClient } from "@tanstack/react-query";
import { DownloadIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";

interface FiscalAssessmentDocumentGroupDocumentProps {
  documentGroupId: string;
  document: MaintenanceFiscalAssessmentDocumentGroupData['documents'][number];
  isAwaitingEstablishmentDocuments?: boolean;
}

const FiscalAssessmentDocumentGroupDocument = ({
  documentGroupId,
  document,
  isAwaitingEstablishmentDocuments,
}: FiscalAssessmentDocumentGroupDocumentProps) => {
  const isApproved = document.last_review?.state === 'approved';
  const isDisapproved = document.last_review?.state === 'disapproved';

  const [fileRemoved, setFileRemoved] = useState(false);

  const queryClient = useQueryClient();

  const {
    mutate: deleteDocument,
    isPending: deleteDocumentIsPending,
  } = useMaintenanceFiscalAssessmentDocumentGroupsUpdateDocumentsDelete({
    options: {
      onSuccess: () => {
        setFileRemoved(true);

        queryClient.invalidateQueries({
          queryKey: ['maintenanceForwarding',],
        });
      },
    },
  });

  const handleRemoveFile = () => {
    deleteDocument({
      documentGroupId,
      documentId: document.id,
    });
  }

  if (fileRemoved) {
    return null;
  }

  return (
    <div className={cn(
      'relative flex flex-col justify-between p-4 gap-5 bg-white-200 rounded-lg',
      isApproved && 'bg-success-op',
      isDisapproved && 'bg-yellow-op-2',
    )}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="italic text-xs overflow-hidden text-ellipsis break-all">
          {getFilenameFromUrl(document.file.url) || 'Documento'}
        </div>
      </div>

      <div className="flex items-end justify-between gap-2">
        <div>
          {isDisapproved && (
            <ResumeItem
              className="text-xs"
              valueClassName="font-semibold text-sm"
              label="Justificativa"
              value={document.last_review?.description || '--'}
            />
          )}
        </div>

        <div className="flex items-center gap-2">
          {isAwaitingEstablishmentDocuments && (
            <>
              {deleteDocumentIsPending ? (
                <Spinner className="fill-white-800 size-4" />
              ) : (
                <button type="button" onClick={handleRemoveFile}>
                  <Trash2Icon strokeWidth={1} />
                </button>
              )}
            </>
          )}

          <a
            href={document.file.url}
            download={getFilenameFromUrl(document.file.url) || 'Documento'}
            target="_blank"
          >
            <DownloadIcon strokeWidth={1} />
          </a>
        </div>
      </div>
    </div>
  );
}

export default FiscalAssessmentDocumentGroupDocument;
